
import React, { useState, useRef, useMemo } from 'react';
import { User, UserRole, Announcement, Attachment } from '../types';
import { MOCK_ANNOUNCEMENTS } from '../mockData';
import { 
  Plus, 
  Send, 
  X, 
  Edit3, 
  Tag, 
  Image as ImageIcon, 
  Video, 
  Paperclip, 
  Trash2,
  Search,
  Filter,
  Maximize2,
  AlertTriangle,
  Clock
} from 'lucide-react';

interface AnnouncementsProps {
  user: User;
}

const Announcements: React.FC<AnnouncementsProps> = ({ user }) => {
  const [announcements, setAnnouncements] = useState<Announcement[]>(MOCK_ANNOUNCEMENTS);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [newTitle, setNewTitle] = useState('');
  const [newContent, setNewContent] = useState('');
  const [newCategory, setNewCategory] = useState<Announcement['category']>('Општо');
  const [attachedMedia, setAttachedMedia] = useState<Attachment | null>(null);
  
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('ALL');
  
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [announcementToDelete, setAnnouncementToDelete] = useState<Announcement | null>(null);
  const [lightboxImage, setLightboxImage] = useState<string | null>(null);
  
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  const fileInputRef = useRef<HTMLInputElement>(null);
  const topRef = useRef<HTMLDivElement>(null);

  const categories: Announcement['category'][] = ['Општо', 'Одржување', 'Итно', 'Состанок'];

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const isVideo = file.type.startsWith('video/');
      const reader = new FileReader();
      reader.onload = () => {
        setAttachedMedia({
          url: reader.result as string,
          type: isVideo ? 'VIDEO' : 'IMAGE',
          name: file.name
        });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleOpenCreate = () => {
    setEditingId(null);
    setNewTitle('');
    setNewContent('');
    setNewCategory('Општо');
    setAttachedMedia(null);
    setIsFormOpen(true);
  };

  const handleOpenEdit = (ann: Announcement) => {
    setEditingId(ann.id);
    setNewTitle(ann.title);
    setNewContent(ann.content);
    setNewCategory(ann.category);
    setAttachedMedia(ann.media || null);
    setIsFormOpen(true);
  };

  const handleSave = () => {
    if (editingId) {
      setAnnouncements(prev => prev.map(ann => 
        ann.id === editingId 
          ? { ...ann, title: newTitle, content: newContent, category: newCategory, media: attachedMedia || undefined }
          : ann
      ));
    } else {
      const newAnn: Announcement = {
        id: `a-${Date.now()}`,
        title: newTitle,
        content: newContent,
        author: user.name,
        date: new Date().toISOString().split('T')[0],
        category: newCategory,
        media: attachedMedia || undefined
      };
      setAnnouncements([newAnn, ...announcements]);
      
      setTimeout(() => {
        topRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
    
    setIsFormOpen(false);
    setEditingId(null);
    setNewTitle('');
    setNewContent('');
  };

  const openDeleteConfirmation = (ann: Announcement) => {
    setAnnouncementToDelete(ann);
    setIsDeleteConfirmOpen(true);
  };

  const confirmDelete = () => {
    if (announcementToDelete) {
      setAnnouncements(prev => prev.filter(ann => ann.id !== announcementToDelete.id));
      setIsDeleteConfirmOpen(false);
      setAnnouncementToDelete(null);
    }
  };

  const toggleContent = (id: string) => {
    const next = new Set(expandedIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setExpandedIds(next);
  };

  const filteredAnnouncements = useMemo(() => {
    return announcements.filter(ann => {
      const matchesSearch = 
        ann.title.toLowerCase().includes(searchQuery.toLowerCase()) || 
        ann.content.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesCategory = categoryFilter === 'ALL' || ann.category === categoryFilter;
      return matchesSearch && matchesCategory;
    });
  }, [announcements, searchQuery, categoryFilter]);

  const getCategoryColor = (cat: string) => {
    switch(cat) {
      case 'Итно': return 'bg-red-50 text-red-600 border-red-100';
      case 'Одржување': return 'bg-amber-50 text-amber-600 border-amber-100';
      case 'Состанок': return 'bg-blue-50 text-blue-600 border-blue-100';
      default: return 'bg-indigo-50 text-indigo-600 border-indigo-100';
    }
  };

  const canEdit = user.role === UserRole.PRESIDENT || user.role === UserRole.DEPUTY || user.role === UserRole.SUPER_ADMIN;

  return (
    <div className="space-y-6 animate-in fade-in duration-500 pb-20" ref={topRef}>
      <header className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-black text-slate-900 tracking-tight">Известувања</h1>
          <p className="text-slate-500 font-medium">Официјални новости и соопштенија за вашата заедница.</p>
        </div>
        {canEdit && (
          <button 
            onClick={handleOpenCreate}
            className="bg-indigo-600 text-white px-6 py-3 rounded-2xl font-black text-sm uppercase tracking-widest hover:bg-indigo-700 transition-all flex items-center shadow-lg shadow-indigo-100 active:scale-95"
          >
            <Plus className="w-5 h-5 mr-2" /> Ново известување
          </button>
        )}
      </header>

      <div className="bg-white p-4 rounded-3xl border border-slate-200 shadow-sm flex flex-col md:flex-row gap-4 items-center">
        <div className="relative flex-1 w-full">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input 
            type="text" 
            placeholder="Пребарај известувања..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-11 pr-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:ring-2 focus:ring-indigo-500 outline-none font-medium transition-all"
          />
        </div>
        <div className="flex items-center space-x-2 w-full md:w-auto">
          <Filter className="w-4 h-4 text-slate-400 ml-2" />
          <select 
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="flex-1 md:flex-none p-3 bg-slate-50 border border-slate-100 rounded-2xl focus:ring-2 focus:ring-indigo-500 outline-none font-bold text-slate-700 appearance-none min-w-[150px]"
          >
            <option value="ALL">Сите категории</option>
            {categories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
          </select>
        </div>
      </div>

      {isFormOpen && (
        <div className="bg-white p-8 rounded-[2.5rem] border border-indigo-100 shadow-2xl space-y-6 animate-in slide-in-from-top duration-300">
          <div className="flex justify-between items-center">
            <h3 className="text-xl font-black text-slate-900 flex items-center">
              {editingId ? 'Уреди го известувањето' : 'Креирај ново известување'}
            </h3>
            <button onClick={() => setIsFormOpen(false)} className="text-slate-400 hover:text-slate-600 p-2 rounded-full hover:bg-slate-100 transition-colors">
              <X className="w-6 h-6" />
            </button>
          </div>
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="md:col-span-2">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mb-2 block">Наслов</label>
                <div className="relative">
                  <input 
                    type="text" 
                    placeholder="Внесете наслов..."
                    value={newTitle}
                    onChange={(e) => setNewTitle(e.target.value)}
                    className="w-full p-4 bg-slate-50 border border-slate-200 rounded-2xl focus:ring-2 focus:ring-indigo-500 outline-none font-bold pr-14"
                  />
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    <button 
                      onClick={() => fileInputRef.current?.click()}
                      title="Прикачи слика или видео"
                      className="text-slate-500 hover:text-slate-700 p-1.5 bg-white rounded-xl border border-slate-100 shadow-sm"
                    >
                      <Paperclip className="w-5 h-5" />
                    </button>
                  </div>
                  <input 
                    type="file" 
                    ref={fileInputRef} 
                    className="hidden" 
                    accept="image/*,video/*"
                    onChange={handleFileUpload} 
                  />
                </div>
              </div>
              <div>
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mb-2 block">Категорија</label>
                <div className="relative">
                  <Tag className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <select 
                    value={newCategory}
                    onChange={(e) => setNewCategory(e.target.value as any)}
                    className="w-full p-4 pl-11 bg-slate-50 border border-slate-200 rounded-2xl focus:ring-2 focus:ring-indigo-500 outline-none appearance-none font-bold text-slate-700"
                  >
                    {categories.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {attachedMedia && (
              <div className="relative rounded-3xl overflow-hidden border border-slate-200 bg-slate-50 flex items-center justify-center min-h-[200px] max-h-[400px] shadow-inner">
                {attachedMedia.type === 'IMAGE' ? (
                  <img src={attachedMedia.url} alt="Прилог" className="max-w-full max-h-[400px] object-contain" />
                ) : (
                  <video src={attachedMedia.url} controls className="max-w-full max-h-[400px]" />
                )}
                <button 
                  onClick={() => setAttachedMedia(null)}
                  className="absolute top-4 right-4 bg-red-600 text-white p-2.5 rounded-full hover:bg-red-700 shadow-xl transition-transform hover:scale-110"
                >
                  <Trash2 className="w-5 h-5" />
                </button>
                <div className="absolute bottom-4 left-4 bg-black/70 backdrop-blur-md text-white px-3 py-1.5 rounded-xl text-[10px] font-black uppercase tracking-widest flex items-center">
                  {attachedMedia.type === 'VIDEO' ? <Video className="w-4 h-4 mr-2" /> : <ImageIcon className="w-4 h-4 mr-2" />}
                  {attachedMedia.name || 'Прилог'}
                </div>
              </div>
            )}

            <div>
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] block mb-2">Содржина</label>
              <textarea 
                placeholder="Напишете ја пораката овде..."
                value={newContent}
                onChange={(e) => setNewContent(e.target.value)}
                className="w-full p-5 bg-slate-50 border border-slate-200 rounded-3xl focus:ring-2 focus:ring-indigo-500 outline-none min-h-[180px] leading-relaxed font-medium"
              />
            </div>
          </div>
          <div className="flex justify-end space-x-4 pt-6 border-t">
            <button 
              onClick={() => setIsFormOpen(false)} 
              className="px-6 py-3 text-slate-500 font-black text-sm uppercase tracking-widest hover:bg-slate-50 rounded-2xl transition-colors"
            >
              Откажи
            </button>
            <button 
              onClick={handleSave}
              className="px-8 py-3 bg-indigo-600 text-white rounded-2xl font-black text-sm uppercase tracking-widest flex items-center shadow-xl shadow-indigo-100 hover:bg-indigo-700 transition-all active:scale-95"
            >
              {editingId ? 'Зачувај' : 'Објави'} <Send className="w-5 h-5 ml-2" />
            </button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {filteredAnnouncements.map(ann => {
          const isExpanded = expandedIds.has(ann.id);
          const contentPreview = ann.content.length > 150 ? ann.content.slice(0, 150) + '...' : ann.content;
          
          return (
            <div key={ann.id} className="bg-white rounded-[2.5rem] border border-slate-200 overflow-hidden shadow-sm hover:shadow-xl hover:border-indigo-200 transition-all group flex flex-col relative animate-in fade-in duration-700">
              {ann.media && (
                <div className="relative w-full aspect-video overflow-hidden bg-slate-100 border-b border-slate-50 group/media">
                  {ann.media.type === 'IMAGE' ? (
                    <>
                      <img src={ann.media.url} alt={ann.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-1000" />
                      <button 
                        onClick={() => setLightboxImage(ann.media!.url)}
                        className="absolute inset-0 bg-black/20 opacity-0 group-hover/media:opacity-100 flex items-center justify-center transition-all backdrop-blur-[2px]"
                      >
                        <div className="bg-white/20 p-4 rounded-full text-white">
                          <Maximize2 className="w-8 h-8" />
                        </div>
                      </button>
                    </>
                  ) : (
                    <video src={ann.media.url} controls className="w-full h-full object-cover" />
                  )}
                  <div className="absolute top-4 left-4">
                     <span className={`px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest border shadow-lg backdrop-blur-md ${getCategoryColor(ann.category)}`}>
                        {ann.category}
                     </span>
                  </div>
                </div>
              )}
              
              <div className="p-8 flex-1 flex flex-col">
                {!ann.media && (
                  <div className="mb-4">
                    <span className={`px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest border ${getCategoryColor(ann.category)}`}>
                      {ann.category}
                    </span>
                  </div>
                )}
                
                <div className="flex justify-between items-start mb-4">
                  <h2 className="text-2xl font-black text-slate-900 leading-tight group-hover:text-indigo-600 transition-colors flex-1">{ann.title}</h2>
                  <div className="flex items-center space-x-1 ml-2">
                    {canEdit && (
                      <>
                        <button 
                          onClick={() => handleOpenEdit(ann)}
                          className="p-2 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-xl transition-all"
                          title="Уреди"
                        >
                          <Edit3 className="w-4 h-4" />
                        </button>
                        <button 
                          onClick={() => openDeleteConfirmation(ann)}
                          className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-xl transition-all"
                          title="Избриши"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </>
                    )}
                  </div>
                </div>

                <div className="text-slate-600 leading-relaxed mb-6 font-medium whitespace-pre-wrap flex-1">
                  {isExpanded ? ann.content : contentPreview}
                  {ann.content.length > 150 && (
                    <button 
                      onClick={() => toggleContent(ann.id)}
                      className="text-indigo-600 font-black text-xs uppercase tracking-widest block mt-3 hover:underline"
                    >
                      {isExpanded ? 'Затвори' : 'Прочитај повеќе'}
                    </button>
                  )}
                </div>

                <div className="flex items-center justify-between pt-6 border-t border-slate-50 mt-auto">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 rounded-2xl bg-indigo-50 flex items-center justify-center text-sm font-black text-indigo-600 uppercase border border-indigo-100 shadow-sm">
                      {ann.author.charAt(0)}
                    </div>
                    <div>
                      <span className="block text-[10px] text-slate-400 font-black uppercase tracking-tighter">Автор</span>
                      <span className="text-sm font-bold text-slate-900">{ann.author}</span>
                    </div>
                  </div>
                  <div className="text-right">
                    <span className="block text-[10px] text-slate-400 font-black uppercase tracking-tighter">Датум</span>
                    <span className="text-xs font-bold text-slate-500 flex items-center">
                       <Clock className="w-3 h-3 mr-1" /> {ann.date}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {filteredAnnouncements.length === 0 && (
        <div className="text-center py-32 bg-white rounded-[3rem] border border-dashed border-slate-300">
           <p className="text-slate-400 font-black uppercase tracking-[0.2em]">Нема пронајдени известувања</p>
        </div>
      )}

      {lightboxImage && (
        <div className="fixed inset-0 z-[150] flex items-center justify-center p-4">
           <div className="absolute inset-0 bg-slate-900/90 backdrop-blur-md" onClick={() => setLightboxImage(null)} />
           <div className="relative max-w-5xl w-full max-h-[90vh] animate-in zoom-in-95 duration-300">
              <button 
                onClick={() => setLightboxImage(null)}
                className="absolute -top-12 right-0 text-white hover:text-indigo-400 p-2"
              >
                <X className="w-8 h-8" />
              </button>
              <img src={lightboxImage} alt="Преглед" className="w-full h-full object-contain rounded-3xl shadow-2xl" />
           </div>
        </div>
      )}

      {isDeleteConfirmOpen && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={() => setIsDeleteConfirmOpen(false)} />
          <div className="relative bg-white p-8 rounded-[2.5rem] shadow-2xl max-w-sm w-full animate-in zoom-in duration-200 border border-red-50">
            <div className="w-16 h-16 bg-red-50 text-red-600 rounded-full flex items-center justify-center mx-auto mb-6">
              <AlertTriangle className="w-8 h-8" />
            </div>
            <h3 className="text-xl font-black text-center text-slate-900 mb-2 uppercase tracking-tight">Избриши известување?</h3>
            <p className="text-slate-500 text-center text-sm mb-8 leading-relaxed font-medium">
              Дали сте сигурни дека сакате да го избришете известувањето <span className="font-black text-slate-900">"{announcementToDelete?.title}"</span>? Оваа акција е неповратна.
            </p>
            <div className="grid grid-cols-2 gap-4">
              <button 
                onClick={() => setIsDeleteConfirmOpen(false)}
                className="py-4 font-black text-[10px] uppercase tracking-widest text-slate-500 bg-slate-100 rounded-2xl hover:bg-slate-200 transition-colors"
              >
                Откажи
              </button>
              <button 
                onClick={confirmDelete}
                className="py-4 font-black text-[10px] uppercase tracking-widest text-white bg-red-600 rounded-2xl hover:bg-red-700 transition-colors shadow-lg shadow-red-100"
              >
                Избриши
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Announcements;
